import React, { FC, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormInputPassword } from '../../inputs/FormInputPassword';
import { CustomMUIButton } from '../../commons/buttons/CustomMUIButton';
import { ButtonClassNames, InputRegexPattern } from '../../../models/Common';

interface SetNewPasswordWithoutConfirmProps {
  submitNewPassword: (password: { password: string; passwordConfirmation: string }) => void;
  error?: string | null;
  buttonText: string;
  description?: boolean;
  helperText?: string;
  pattern?: RegExp;
  isLoading: boolean;
}

const defaultProps = {
  error: null,
  description: true,
  helperText: undefined,
  pattern: InputRegexPattern.LettersNumbersUnderscore,
};

export const SetNewPasswordWithoutConfirm: FC<SetNewPasswordWithoutConfirmProps> = ({
  submitNewPassword,
  error,
  buttonText,
  description,
  helperText,
  pattern,
  isLoading,
}) => {
  const { t } = useTranslation();
  const methods = useForm();
  const {
    handleSubmit,
    register,
    watch,
    setError,
    reset,
    formState,
    formState: { isSubmitSuccessful },
  } = methods;
  const password = useRef({});
  password.current = watch('password', '');

  const passwordConfirmRule = {
    validate: (value: string) => value === password.current || t('validation.passwordsDoNotMatch').toString(),
  };

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({
        password: '',
        passwordConfirmation: '',
      });
    }
    if (error) {
      setError('password', {
        type: 'server',
        message: error,
      });
      setError('passwordConfirmation', {
        type: 'server',
        message: '',
      });
    }
  }, [formState, reset, submitNewPassword]);

  return (
    <FormProvider {...methods}>
      {description && (
        <p className="set-new-password__text">
          <strong>{t('auth.from6To16CharactersInLatinAndNumbersOfSpecialCharactersOnlyAllowed_')} </strong>
        </p>
      )}
      <form className="set-new-password__form form" onSubmit={handleSubmit(submitNewPassword)}>
        <FormInputPassword
          name="password"
          label={t('auth.enterNewPassword')}
          className="primary"
          ref={(e) => register(e?.value || '')}
          helperText={helperText}
          isRequired
          pattern={pattern}
          minLength={6}
          maxLength={16}
        />
        <FormInputPassword
          name="passwordConfirmation"
          label={t('auth.repeatNewPassword')}
          className="primary"
          ref={() => register('passwordConfirmation', passwordConfirmRule)}
          isRequired
        />
        <div className="set-new-password__form__btn-group btn-group">
          <CustomMUIButton
            label={buttonText}
            type="submit"
            className={ButtonClassNames.PrimaryFulfilled}
            isBold
            isLoading={isLoading}
          />
        </div>
      </form>
    </FormProvider>
  );
};
SetNewPasswordWithoutConfirm.defaultProps = defaultProps;
